import { zodResolver } from '@hookform/resolvers/zod';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import {
    Alert,
    Box,
    Container,
    Divider,
    Grid,
    Paper,
    Stack,
    Typography,
    useTheme,
} from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { differenceInHours, format, isSameDay, parse } from 'date-fns';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    FieldErrors,
    FieldValues,
    FormProvider,
    useForm,
    useWatch,
} from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import {
    useBeforeUnload,
    useBlocker,
    useLocation,
    useNavigate,
} from 'react-router-dom';
import * as z from 'zod';
import { setBookerObject } from '../../../../app/Slices/BookerSlice';
import {
    selectReservationPolicies,
    setReservationPolicies,
} from '../../../../app/Slices/ReservationPoliciesSlice';
import {
    useCalculateFeesMutation,
    useCreateJobRequestMutation,
    useGetAllGuestAttendeesQuery,
    useGetAssetCalendarAvailabilityQuery,
    useGetAvailabilityQuery,
    useGetBookingCapAvailabilityMutation,
    useGetIndividualClientQuery,
    useGetProfileDetailsQuery,
    useSubmitRecurringReservationMutation,
    useSubmitReservationMutation,
} from '../../../../app/Slices/SevenDayApiSlice';
import BackButton from '../../../../layout/styledcomponents/Buttons/BackButton';
import { CustomButton } from '../../../../layout/styledcomponents/CustomButton';
import { CustomIconButton } from '../../../../layout/styledcomponents/CustomIconButton';
import CustomInputLabel from '../../../../layout/styledcomponents/CustomInputLabel';
import UserRole from '../../../../shared/AuthRoles';
import {
    addHoursToTime,
    getDateArray,
    getMinTimeFromArray,
} from '../../../../shared/utils';
import {
    AvailabilityMode,
    CleaningFeeType,
    ExperienceType,
    SpaceType,
} from '../../../enums/experience';
import { CheckinDetails } from '../../../shared/FormCalendar';
import FormSelect from '../../../shared/FormSelect';
import FormTextField from '../../../shared/FormTextField';
import toast from '../../../shared/Toast';
import { ExperiencePolicy, ReservationPolicy } from '../../../types/policy';
import CancelModal from '../../Experiences/Owner/CancelModal';
import ReservationSpaces from '../Spaces/ReservationSpaces';
import AdditionalServices from './FormSections/ReservationRequestSections/AdditionalServices';
import AttendeesSection from './FormSections/ReservationRequestSections/AttendeesSection';
import BookerSelect from './FormSections/ReservationRequestSections/BookerSelect';
import GuestsSection from './FormSections/ReservationRequestSections/GuestSection';
import NotesField from './FormSections/ReservationRequestSections/NotesField';
import RightSummary from './FormSections/ReservationRequestSections/RightSummary';
import TimeSelectors from './FormSections/ReservationRequestSections/TimeSelectors';
import VisitTypeField from './FormSections/ReservationRequestSections/VisitTypeField';
import WorkspaceTypeAndMeetingRoom from './FormSections/ReservationRequestSections/WorkspaceTypeAndMeetingRoom';
import ContractorPolicies from './ReservationJobs/ContractorPolicies';
import Drivers from './Shared/Drivers/Drivers';
import ReservationPolicies from './Shared/ReservationPolicies';
import {
    getAvailableSpaces,
    getCheckinTimeArray,
    getCheckoutTimeArray,
    shouldValidateSameDayHireHours,
} from './Shared/reservationUtils';
import { getDefaultValues, getRequestSchema } from './Shared/schema';
import { reservationRequestFormFieldsSx } from '../EditReservation/reservationEditFieldStyles';
import { firstReservationFormErrorMessage } from '../EditReservation/reservationEdit.utils';
import { usesNirovision } from '../../Experiences/utils';
import { OwnerAttendeesSection } from './FormSections/ReservationRequestSections/OwnerAttendeesSection';
import { BookingSetMenu } from '../../../types/set-menu';
import { ProviderType } from '../../../../app/Slices/SevenDayApiTypes';

const toISODate = (d: string | Date) => format(new Date(d), 'yyyy-MM-dd');
const toAU = (d: string | Date) => format(new Date(d), 'dd/MM/yyyy');
const hhmm = (h: number) => `${String(h).padStart(2, '0')}:00`;
const hoursBetween = (a?: string, b?: string) =>
    a && b
        ? differenceInHours(
              parse(a, 'HH:mm', new Date()),
              parse(b, 'HH:mm', new Date()),
          )
        : 0;

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const lookupEmailById = (id: any, list: any[]) => {
    const u = Array.isArray(list)
        ? list.find((g: any) => g?.id === id)
        : undefined;
    return (u?.email || '').trim();
};
const extractEmail = (item: any, list: any[]) => {
    if (!item) return '';
    if (typeof item === 'string') return item.trim();
    if (item.email) return String(item.email).trim();
    if (item.user?.email) return String(item.user.email).trim();
    if (item.value?.email) return String(item.value.email).trim();
    if (item.id) return lookupEmailById(item.id, list);
    return '';
};

const normalizeSpaceType = (v?: string) =>
    (v ?? '')
        .toLowerCase()
        .replace(/\s+/g, '')
        .replace(/_/g, '')
        .replace('meetingroom', 'meetingRoom')
        .replace('privateoffice', 'privateOffice')
        .replace('entirevenue', 'entireVenue') as string;

export const shouldShowSpaces = ({
    experienceDetails,
    hasByZoneCleaningFee,
    spaceType,
    isMeetingRoom,
    isOwner,
    isOnBehalf,
}: {
    experienceDetails: any;
    hasByZoneCleaningFee: boolean;
    spaceType: string;
    isMeetingRoom: boolean;
    isOwner: boolean;
    isOnBehalf: boolean;
}) => {
    const has = (experienceDetails?.spaces?.length ?? 0) > 0;
    const eligible =
        (experienceDetails.type === 'property' &&
            (hasByZoneCleaningFee ||
                experienceDetails.spaces.some(
                    (space: any) =>
                        space.spaceType === SpaceType.Bedroom &&
                        space.sleepingCapacity > 0,
                ))) ||
        (experienceDetails.type === 'boat' && hasByZoneCleaningFee) ||
        (experienceDetails.type === 'commercial' &&
            (spaceType === 'meetingRoom' ||
                spaceType === 'privateOffice' ||
                isMeetingRoom ||
                (hasByZoneCleaningFee && spaceType === 'entireVenue')));
    return has && eligible && (!isOwner || (isOwner && isOnBehalf));
};
export const getSpacesWithAvailability = ({
    experienceDetails,
    hasByZoneCleaningFee,
    spaceType,
}: {
    experienceDetails: any;
    hasByZoneCleaningFee: boolean;
    spaceType: string;
}) => {
    return experienceDetails?.spaces.filter((space: any) => {
        if (!hasByZoneCleaningFee) {
            if (experienceDetails?.type === 'commercial') return true;
            return space.sleepingCapacity > 0;
        }
        if (experienceDetails?.type !== 'commercial')
            return (
                space.cleaningFee > 0 || space.spaceType === SpaceType.Bedroom
            );
        if (
            experienceDetails?.type === 'commercial' &&
            spaceType === 'entireVenue'
        )
            return space.cleaningFee > 0;
        return true;
    });
};

interface Params {
    reservationDetails: any;
    isItineraryItemForm?: boolean;
    submitRef?: React.MutableRefObject<(() => Promise<void>) | null>;
    /**
     *
     * @param response Booking Object
     */
    onReservationSuccess?: (response: any) => void;
    mainBooking?: { id: number; startDate: string; endDate: string };
    state?: any;
    existingReservation?: { id: number };
    isAddOn?: boolean;
}

export default function ReservationRequestContent(params: Params) {
    const experienceDetails = params?.reservationDetails;
    const loggedInUser = UserRole();
    const isOwner = loggedInUser?.role?.includes('owner');
    const isGuestAdmin = ['guest_admin', 'guest_department_admin'].includes(
        loggedInUser?.role,
    );
    const isItineraryItemForm = params?.isItineraryItemForm;
    const submitRef = params?.submitRef;
    const onReservationSuccess = params?.onReservationSuccess;
    const mainBookingId = params?.mainBooking?.id;
    const isAddOn = params?.isAddOn;
    const theme = useTheme();

    const location = useLocation();
    const state = isItineraryItemForm ? params?.state : location?.state;
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const startDate = toISODate(state?.dates[0].startDate);
    const endDate = toISODate(state?.dates[0].endDate);
    const [defaultCheckinTime, setDefaultCheckinTime] = useState<string>();
    const [defaultCheckoutTime, setDefaultCheckoutTime] = useState<string>();
    const [defaultCheckoutBefore, setDefaultCheckoutBefore] =
        useState<string>();
    const [dates, setDates] = useState(
        (state?.reservationData?.dates || state?.dates)?.map(
            (d: any, i: number) => ({
                id: i,
                startDate: toISODate(d.startDate),
                endDate: toISODate(d.endDate),
            }),
        ),
    );

    const routeSpaceType = normalizeSpaceType(state?.commercialSpaceType);

    const usesFaceRec = usesNirovision(experienceDetails);

    const selectedSchema = getRequestSchema(
        experienceDetails,
        loggedInUser?.role,
    );
    type Schema = z.infer<typeof selectedSchema>;

    const baseDefaults = getDefaultValues(
        experienceDetails,
        startDate !== endDate,
        loggedInUser,
    );

    const [limitedDates, setLimitedDates] = useState<
        Record<string, CheckinDetails>
    >({});

    const { data: availability, isFetching: isFetchingAvailability } =
        useGetAssetCalendarAvailabilityQuery({
            url: `/modules/${experienceDetails?.id}/calendar`,
        });

    useEffect(() => {
        if (availability?.limited) {
            const mappedLimitedDates = availability.limited.reduce(
                (acc: Record<string, CheckinDetails>, day: any) => {
                    acc[day.date] = {
                        checkinFrom: day.checkinFrom,
                        checkoutBefore: day.checkoutBefore,
                    };
                    return acc;
                },
                {},
            );

            setLimitedDates(mappedLimitedDates);
        }
    }, [availability]);

    useEffect(() => {
        let checkinDetails;
        let checkoutDetails;
        let maxCheckoutTime = experienceDetails?.checkoutTime;
        if (isFetchingAvailability || !experienceDetails) return;

        if (startDate) checkinDetails = limitedDates[startDate];
        if (endDate) checkoutDetails = limitedDates[endDate];

        if (checkinDetails?.checkinFrom) {
            setDefaultCheckinTime(checkinDetails.checkinFrom);
        } else {
            setDefaultCheckinTime(experienceDetails?.checkinTime);
        }

        if (experienceDetails?.maxHireHours <= 24) {
            maxCheckoutTime = addHoursToTime(
                checkinDetails?.checkinFrom
                    ? checkinDetails?.checkinFrom
                    : experienceDetails?.checkinTime,
                experienceDetails?.maxHireHours,
            );
        }

        if (
            (checkoutDetails?.checkoutBefore && !checkoutDetails.checkinFrom) ||
            (checkoutDetails?.checkoutBefore &&
                checkoutDetails.checkinFrom &&
                startDate !== endDate)
        ) {
            setDefaultCheckoutTime(
                getMinTimeFromArray([
                    checkoutDetails.checkoutBefore,
                    maxCheckoutTime,
                ]),
            );

            setDefaultCheckoutBefore(checkoutDetails.checkoutBefore);
        } else {
            setDefaultCheckoutTime(
                getMinTimeFromArray([
                    experienceDetails?.checkoutTime,
                    maxCheckoutTime,
                ]),
            );

            setDefaultCheckoutBefore(experienceDetails?.checkoutTime);
        }
    }, [limitedDates, experienceDetails]);

    const methods = useForm<Schema>({
        resolver: zodResolver(selectedSchema),
        mode: 'onChange',
        defaultValues: {
            ...baseDefaults,
            bookingInfo: {
                ...(baseDefaults as any).bookingInfo,
                spaceType:
                    routeSpaceType ??
                    (normalizeSpaceType(
                        (baseDefaults as any).bookingInfo?.spaceType,
                    ) as any),
                reqMeetingRoom: state?.reqMeetingRoom,
            },
        } as any,
        shouldUnregister: false,
        shouldFocusError: true,
    });

    const {
        control,
        handleSubmit,
        setValue,
        formState: { isSubmitting, errors: formErrors, isDirty },
        reset,
        watch,
    } = methods;

    if (Object.keys(formErrors).length) {
        console.error(formErrors);
    }

    const jobs = useWatch({ control, name: 'jobs' });

    useEffect(() => {
        if (state?.reservationData) {
            const { bookingInfo } = state.reservationData;
            const { overnightGuests, ...rest } = bookingInfo;
            reset({
                ...state?.reservationData,
                bookingInfo:
                    bookingInfo.moduleType !== 'boat' && !overnightGuests
                        ? { ...rest, guests: bookingInfo?.adults }
                        : bookingInfo,
                policies,
            } as any);
        }
        if (state?.times) {
            setValue('bookingInfo.checkinTime', state.times.checkin as any);
            setValue('bookingInfo.checkoutTime', state.times.checkout as any);
        }
        if (state?.notes) {
            setValue('bookingInfo.notes', state.notes);
        }
        if (state?.waitListId) {
            setValue('waitListId', state.waitListId);
        }
    }, []);

    const [errors, setErrors] = useState<Record<string, string>>({});
    const [fees, setFees] = useState<any>();
    const [conflictError, setConflictError] = useState('');
    const [navigationBlockerOpen, setNavigationBlockerOpen] =
        useState<boolean>(false);
    const isSubmittingRef = useRef(false);

    // Block navigation when form has unsaved changes
    const blocker = useBlocker(
        ({ currentLocation, nextLocation }) =>
            isDirty &&
            !isSubmitting &&
            !isSubmittingRef.current &&
            currentLocation.pathname !== nextLocation.pathname,
    );

    // Warn user when trying to close/refresh browser with unsaved changes
    useBeforeUnload(
        React.useCallback(
            (event) => {
                if (isDirty && !isSubmitting && !isSubmittingRef.current) {
                    event.preventDefault();
                    return (event.returnValue = '');
                }
            },
            [isDirty, isSubmitting],
        ),
    );

    // Show modal when navigation is blocked
    useEffect(() => {
        if (blocker.state === 'blocked') {
            setNavigationBlockerOpen(true);
        }
    }, [blocker.state]);

    const handleProceedNavigation = () => {
        setNavigationBlockerOpen(false);
        blocker.proceed?.();
    };

    const handleCancelNavigation = () => {
        setNavigationBlockerOpen(false);
        blocker.reset?.();
    };

    const [calculateFees] = useCalculateFeesMutation();
    const [requestReservation] = useSubmitReservationMutation();
    const [requestRecurringReservation] =
        useSubmitRecurringReservationMutation();

    const { data: guestUsers, isFetching: isFetchingGuestUsers } =
        useGetIndividualClientQuery(`/guests/${loggedInUser?.company}/users`, {
            skip: !loggedInUser.role.includes('guest'),
        });

    const { data: guestAttendees } = useGetAllGuestAttendeesQuery(
        loggedInUser?.company,
        {
            skip: !loggedInUser.role.includes('guest'),
        },
    );
    const { data: profileDetails } = useGetProfileDetailsQuery('/profile');

    const bookerId = useWatch({ control, name: 'booker.id' });
    const bookingInfo = useWatch({
        control,
        name: 'bookingInfo',
        defaultValue: {
            ...(baseDefaults as any).bookingInfo,
            spaceType:
                routeSpaceType ??
                (normalizeSpaceType(
                    (baseDefaults as any).bookingInfo?.spaceType,
                ) as any),
        } as any,
    });

    const watchedCheckinTime = watch('bookingInfo.checkinTime');
    const watchedCheckoutTime = watch('bookingInfo.checkoutTime');
    const commercialSpaceype = watch('bookingInfo.spaceType');

    useEffect(() => {
        if (state?.reservationData) {
            const { bookingInfo } = state.reservationData;
            const { overnightGuests, ...rest } = bookingInfo;
            reset({
                ...state?.reservationData,
                bookingInfo:
                    bookingInfo.moduleType !== 'boat' && !overnightGuests
                        ? { ...rest, guests: bookingInfo?.adults }
                        : bookingInfo,
                policies,
            } as any);
        }
        if (state?.times) {
            setValue('bookingInfo.checkinTime', state.times.checkin as any);
            setValue('bookingInfo.checkoutTime', state.times.checkout as any);
        } else if (!watchedCheckinTime) {
            setValue('bookingInfo.checkinTime', defaultCheckinTime);
            setValue('bookingInfo.checkoutTime', defaultCheckoutTime);
        }
    }, [defaultCheckinTime, defaultCheckoutTime]);

    const {
        spaceType: rawSpaceType,
        reqMeetingRoom: isMeetingRoom,
        checkinTime,
        checkoutTime,
        requiresCatering: isCatering,
        requiresMassages: isMassage,
        requiresBeverages: isAlcohol,
        cateringNotes: cateringDetails,
        beverageNotes: beverageDetails,
        massageNotes: massageDetails,
        notes: additionalNotes,
        adults: adultCount,
        children: childCount,
        overnightGuests: overnightGuestCount,
        guests: guestCount,
        drivers,
        driverSameAsBooker,
    } = bookingInfo || ({} as any);

    const typeKey: string = useMemo(
        () => normalizeSpaceType(rawSpaceType),
        [rawSpaceType],
    );

    const isOnBehalf = useWatch({ control, name: 'onBehalf' });
    const spaces = useWatch({ control, name: 'spaces' });
    const visitType = experienceDetails?.visitType;
    const [getBookingCapAvailability] = useGetBookingCapAvailabilityMutation();
    const policies = useSelector(selectReservationPolicies);
    const watchedPolicy = useWatch({ control, name: 'policy' });

    useEffect(() => {
        const reservationPolicies: ReservationPolicy[] =
            experienceDetails?.policies?.map((p: ExperiencePolicy) => ({
                policyId: p?.policyId,
                checked: false,
                checkedAt: undefined,
                name: p?.name,
                description: p?.description,
                requiresConsent: p?.requiresConsent,
            })) || [];

        if (experienceDetails?.policy) {
            const nextPolicyId = experienceDetails?.policy?.policyId;
            const currentPolicyId = watchedPolicy?.policyId;
            const keepChecked =
                currentPolicyId === nextPolicyId
                    ? watchedPolicy?.checked
                    : false;
            setValue('policy', {
                checked: Boolean(keepChecked),
                requiresConsent: experienceDetails?.policy.requiresConsent,
                description: experienceDetails?.policy.description,
                name: experienceDetails?.policy.name,
                policyId: experienceDetails?.policy.policyId,
                cancellationType: experienceDetails.policy?.cancellationType,
            } as any);
        }

        dispatch(setReservationPolicies(reservationPolicies));
        if (loggedInUser?.role === 'guest_user')
            checkAvailability(loggedInUser?.sub);
    }, [
        dispatch,
        experienceDetails?.policies,
        experienceDetails?.policy,
        loggedInUser?.role,
        loggedInUser?.sub,
        setValue,
        watchedPolicy?.checked,
        watchedPolicy?.policyId,
    ]);

    const checkinT = state?.times?.checkinFrom || defaultCheckinTime;
    const checkoutT = state?.times?.checkoutBefore || defaultCheckoutBefore;

    useEffect(() => {
        if (experienceDetails?.visitType === 'personal') {
            setValue(
                'bookingInfo.overnightGuests',
                (adultCount + childCount) as any,
            );
        }
    }, [adultCount, childCount]);

    const MAX_GUEST_COUNT_TEST =
        experienceDetails?.type === 'activity'
            ? guestCount >= (experienceDetails?.details.quantity || 0)
            : (adultCount || 0) + (childCount || 0) >=
              (experienceDetails?.details.maxGuests || 0);

    const showStaffBlocks =
        experienceDetails?.visitType !== 'personal' &&
        experienceDetails?.type !== ExperienceType.Vehicle &&
        (experienceDetails?.type !== ExperienceType.Commercial ||
            typeKey === 'meetingRoom' ||
            typeKey === 'entireVenue' ||
            isMeetingRoom ||
            (isOwner && !isOnBehalf));

    const { data: commercialAvailability } = useGetAvailabilityQuery(
        {
            url: `/modules/commercial/${experienceDetails.id}/get-availability`,
            body: {
                startDate,
                endDate,
                checkinTime: checkinTime || experienceDetails?.checkinTime,
                checkoutTime: checkoutTime || experienceDetails?.checkoutTime,
                bookerId: loggedInUser?.role.includes('owner')
                    ? undefined
                    : bookerId,
            },
        },
        { skip: experienceDetails.type !== ExperienceType.Commercial },
    );

    const availableSpaces = useMemo(
        () => getAvailableSpaces(commercialAvailability, experienceDetails),
        [commercialAvailability, experienceDetails?.details],
    );

    const checkAvailability = (value: any) => {
        getBookingCapAvailability({
            moduleId: experienceDetails?.id,
            startDate,
            bookerId: value ?? loggedInUser.sub,
        }).then((data: any) => {
            if (data?.error?.status === 400) {
                const msg =
                    data?.error?.data?.message +
                    (isGuestAdmin
                        ? ' Please change the dates or the primary guest.'
                        : ' Please change the dates.');
                setErrors((p) => ({ ...p, bookingCaps: msg }));
            } else {
                setErrors(({ bookingCaps, ...rest }) => rest);
            }
        });
    };

    const compareCheckinSettings = getCheckinTimeArray(
        startDate,
        endDate,
        checkoutT,
        checkinT,
        experienceDetails,
    );
    const compareCheckoutSettings = getCheckoutTimeArray(
        startDate,
        endDate,
        checkoutT,
        checkinT,
        experienceDetails,
        checkinTime,
    );

    useEffect(() => {
        const min = experienceDetails.minHireHours;
        const max = experienceDetails.maxHireHours;
        const timeToAdd = hhmm(min);
        const timeToSubtract = hhmm(max);
        const sel = hoursBetween(checkoutTime, checkinTime);
        let msg = '';
        if (!checkinTime || !checkoutTime) return;
        const sameDay = startDate === endDate;
        const validateSameDayHours = shouldValidateSameDayHireHours(min, max);
        const belowMin =
            validateSameDayHours &&
            sel < parseInt(timeToAdd) &&
            sameDay &&
            checkinTime < checkoutTime;
        const aboveMax =
            validateSameDayHours &&
            sel > parseInt(timeToSubtract) &&
            sameDay &&
            checkinTime <= checkoutTime &&
            timeToSubtract !== '00:00';
        if (belowMin)
            msg = `Your selection is below the allowed hire time for this experience ${min} hrs`;
        else if (aboveMax)
            msg = `Your selection exceeds the allowed hire time for this experience ${min}-${max} hrs`;
        else if (sameDay && checkinTime > checkoutTime)
            msg = 'Checkin time cannot be greater than checkout time';
        if (msg) setErrors((p) => ({ ...p, checkoutTime: msg }));
        else setErrors(({ checkoutTime, ...rest }) => rest);
    }, [checkinTime, checkoutTime, startDate, endDate]);

    const hasByZoneCleaningFee = experienceDetails?.charges?.find(
        (c: any) =>
            c.chargeType === 'cleaningFee' &&
            c.cleaningFeeType === CleaningFeeType.ByZone,
    );

    useEffect(() => {
        checkAvailability(bookerId);
        const obj = guestUsers?.find((o: any) => o.id === bookerId);
        dispatch(setBookerObject(obj));
    }, [bookerId, guestUsers]);

    useEffect(() => {
        if (Object.keys(errors).includes('bookingCaps')) {
            toast.error(errors.bookingCaps, {
                duration: Infinity,
                customId: 'bookingCapWarning',
            });
        } else {
            toast.remove('bookingCapWarning');
        }
    }, [errors]);

    useEffect(() => {
        if (conflictError) {
            toast.error(conflictError, {
                customId: 'conflictError',
                duration: Infinity,
            });
        } else {
            toast.remove('conflictError');
        }
    }, [conflictError]);

    useEffect(() => {
        if (
            (experienceDetails?.type === 'commercial' && !typeKey) ||
            !startDate ||
            !visitType
        )
            return;

        calculateFees({
            bookerType: loggedInUser?.role?.includes('owner')
                ? 'owner'
                : 'guest',
            module: { id: experienceDetails?.id },
            startDate,
            endDate,
            onBehalf: isOnBehalf,
            bookingInfo: {
                moduleType: experienceDetails?.type,
                visitType,
                checkinTime: checkinTime || '08:00',
                checkoutTime: checkoutTime || '17:00',
                adults: adultCount ?? 0,
                children: childCount ?? 0,
                overnightGuests: overnightGuestCount ?? 0,
                requiresCatering: isCatering,
                cateringNotes: cateringDetails,
                requiresBeverages: isAlcohol,
                beverageNotes: beverageDetails,
                requiresMassages: isMassage,
                massageNotes: massageDetails,
                notes: additionalNotes,
                desks: typeKey === 'desk' ? 1 : 0,
                privateOffices: typeKey === 'privateOffice' ? 1 : 0,
                meetingRooms:
                    typeKey === 'meetingRoom' ||
                    (isMeetingRoom && typeKey !== 'entireVenue')
                        ? 1
                        : 0,
                entireVenue: typeKey === 'entireVenue',
                guests: adultCount || guestCount,
                drivers: [],
                driverSameAsBooker,
            },
            setMenus: [],
            requiresServices: false,
            policies,
            staffAttending: [],
            externalAttendees: [],
            spaces,
        }).then((d: any) => {
            if (!d?.error) setFees(d.data);
        });
    }, [
        adultCount,
        childCount,
        isAlcohol,
        isCatering,
        checkinTime,
        checkoutTime,
        isMeetingRoom,
        overnightGuestCount,
        typeKey,
        isMassage,
        startDate,
        visitType,
        guestCount,
        spaces,
        drivers,
        driverSameAsBooker,
        isOnBehalf,
        startDate,
        endDate,
    ]);

    useEffect(() => {
        if (
            experienceDetails.type !== 'commercial' ||
            !availableSpaces.length ||
            !typeKey
        )
            return;
        const available = availableSpaces
            .filter((s) => !s.label.includes('unavailable'))
            .map((s) => s.value);
        if (commercialAvailability?.entireVenueAvailable)
            available.push('entireVenue');
        if (available.length && !available.includes(typeKey as any)) {
            toast.warning(
                `The space type you have selected is not available. You can create a waitlist request for it`,
                {
                    duration: 10000,
                    customId: 'spaceTypeNotAvailable',
                    closeButton: true,
                },
            );
            toast.remove('spaceTypeAvailable');
            // Bypass unsaved changes warning when redirecting to waitlist
            isSubmittingRef.current = true;
            navigate(
                `/experiences/waitListRequest/${experienceDetails?.type.toLowerCase()}/${experienceDetails?.name
                    .replace(/ /g, '')
                    .toLowerCase()}/${experienceDetails?.id}`,
                {
                    state: {
                        ...state,
                        commercialSpaceType: typeKey,
                    },
                },
            );
        }
    }, [typeKey, availableSpaces]);

    useEffect(() => {
        setConflictError('');
        if (
            commercialAvailability &&
            !commercialAvailability?.entireVenueAvailable &&
            isOwner &&
            !isOnBehalf
        ) {
            setConflictError(
                `You cannot block the selected period. There are existing bookings during this timeframe. Please choose a different period.`,
            );
        } else if (
            commercialAvailability &&
            !commercialAvailability?.entireVenueAvailable &&
            typeKey === 'entireVenue'
        ) {
            setConflictError(
                `You cannot book the entire venue. There are existing bookings during this timeframe. Please choose a different period.`,
            );
        } else if (
            (commercialAvailability?.conflictingBookingIds?.length || 0) > 0 &&
            isGuestAdmin
        ) {
            setConflictError(
                `You already have a booking for the selected dates. Please change the dates or the booker.`,
            );
        } else if (
            (commercialAvailability?.conflictingBookingIds?.length || 0) > 0 &&
            !isGuestAdmin &&
            !isOwner
        ) {
            setConflictError(
                `You already have a booking for the selected dates. Please change the dates`,
            );
        }
    }, [
        commercialAvailability,
        experienceDetails?.details,
        loggedInUser?.role,
        isOnBehalf,
        typeKey,
        isGuestAdmin,
    ]);

    useEffect(() => {
        dispatch(setBookerObject(profileDetails));
    }, [profileDetails]);

    useEffect(() => {
        if (typeKey === 'entireVenue' || typeKey === 'meetingRoom')
            setValue('bookingInfo.reqMeetingRoom', false as any);
    }, [typeKey]);

    const allJobsArray = Object.values(jobs ?? {}) as any[];

    const contractorJobsArray = allJobsArray.filter(
        (job: any) => job.providerType === ProviderType.Contractor,
    );

    const setMenuJobsArray: BookingSetMenu[] = allJobsArray.filter(
        (job: any) => job.providerType === ProviderType.Owner,
    );
    const [createJob] = useCreateJobRequestMutation();

    const onInvalid = React.useCallback((errs: FieldErrors) => {
        const staffAttendeeMsg = (errs?.staffAttending as { message?: string })
            ?.message;
        const jobsMsg = (errs?.jobs as { message?: string })?.message;
        const msg =
            (typeof staffAttendeeMsg === 'string' && staffAttendeeMsg.length > 0
                ? staffAttendeeMsg
                : undefined) ??
            (typeof jobsMsg === 'string' && jobsMsg.length > 0
                ? jobsMsg
                : undefined) ??
            firstReservationFormErrorMessage(errs) ??
            'Please complete all required fields before reserving.';
        toast.error(msg, { duration: 8000 });
    }, []);

    const onSubmit = async (data: FieldValues) => {
        const staffRaw = Array.isArray(data?.staffAttending)
            ? data.staffAttending
            : [];
        const staffObjs = staffRaw.map((x: any) => ({
            email: extractEmail(x, guestAttendees || []),
        }));

        if (staffObjs.some((o) => !emailRegex.test(o.email))) {
            toast.error('Please add a valid email for each staff attendee.');
            return;
        }

        let additionalBookingInfo = {};
        if (
            experienceDetails?.type === 'commercial' &&
            (data as any)?.bookingInfo
        ) {
            additionalBookingInfo = {
                desks: typeKey === 'desk' ? 1 : 0,
                privateOffices: typeKey === 'privateOffice' ? 1 : 0,
                meetingRooms:
                    typeKey === 'meetingRoom' ||
                    ((data as any).bookingInfo.reqMeetingRoom &&
                        typeKey !== 'entireVenue')
                        ? 1
                        : 0,
                entireVenue: typeKey === 'entireVenue',
            };
        }

        if (Object.keys(errors)?.length > 0 || conflictError) return;
        const privateEnquiry: boolean = !!state?.privateEnquiry;

        const request = {
            ...data,
            ...(isItineraryItemForm
                ? { mainBooking: { id: mainBookingId } }
                : {}),
            staffAttending: staffObjs,
            bookingInfo: {
                moduleType: experienceDetails?.type,
                overnightGuests: 0,
                ...(data as any).bookingInfo,
                ...additionalBookingInfo,
            },
            bookerType: loggedInUser?.role?.includes('owner')
                ? 'owner'
                : 'guest',
            module: { id: experienceDetails?.id },
            ...(dates.length > 1
                ? { dates }
                : {
                      startDate: dates[0].startDate,
                      endDate: dates[0].endDate,
                  }),
            policy: (data as any).policy,
            channelId: privateEnquiry ? undefined : state?.channelId,
            privateMessages: privateEnquiry
                ? [{ channelId: state?.channelId }]
                : [],
            isAddOn,
            setMenus: setMenuJobsArray.map((bookingSetMenu: BookingSetMenu) => {
                return {
                    ...bookingSetMenu,
                    id:
                        typeof bookingSetMenu.id === 'number'
                            ? bookingSetMenu.id
                            : undefined,
                };
            }),
        };

        try {
            const reserve =
                dates.length === 1
                    ? requestReservation
                    : requestRecurringReservation;

            const response = await reserve(request as any).unwrap();

            const responseError =
                (response as any)?.error ??
                (Array.isArray(response)
                    ? (response as any)[0]?.error
                    : undefined);

            if (responseError === 'users.invalid_domain') {
                toast.error(
                    'Only staff with an email address from your company domain can be added in Staff Attending.',
                    {
                        duration: 10000,
                        customId: 'emailAttendeesError',
                        closeButton: true,
                    },
                );
                return;
            }

            isSubmittingRef.current = true;

            if (contractorJobsArray.length > 0) {
                for (const job of contractorJobsArray) {
                    await createJob({
                        ...job,
                        booking: { id: (response as any).id },
                        id: typeof job.id === 'number' ? job.id : undefined,
                    }).unwrap();
                }
            }

            toast.success(
                experienceDetails?.requiresBookingApproval
                    ? `Reservation request submitted`
                    : `Reservation submitted`,
            );

            if (isItineraryItemForm) {
                onReservationSuccess?.(response);
            }

            if (!isItineraryItemForm) {
                if (dates.length === 1) {
                    navigate(
                        `/reservationRequest/submitted/${(response as any).id}`,
                        {
                            state: {
                                page: 'singleReservation',
                                id: (response as any).id,
                            },
                        },
                    );
                } else {
                    navigate(
                        `/reservationRequest/submitted/ai-assistant/${
                            (response as any)[0]?.recurringId
                        }`,
                        {
                            state: {
                                page: 'recurringReservation',
                                id: (response as any)[0]?.recurringId,
                            },
                        },
                    );
                }
            }
        } catch (error: any) {
            const apiError = error?.data?.error;

            if (apiError === 'users.invalid_domain') {
                toast.error(
                    'Only staff with an email address from your company domain can be added in Staff Attending.',
                    {
                        duration: 10000,
                        customId: 'emailAttendeesError',
                        closeButton: true,
                    },
                );
                return;
            }

            if (apiError === 'booking_cap_exceeded') {
                toast.error(`${error?.data?.message}`);
                return;
            }

            toast.error('Failed to submit reservation');
        }
    };

    if (submitRef) {
        submitRef.current = () => handleSubmit(onSubmit, onInvalid)();
    }

    const columns: GridColDef[] = [
        {
            field: 'startDate',
            headerName: 'Start Date',
            flex: 0.3,
            minWidth: 100,
            sortable: false,
            valueFormatter: ({ value }) => toAU(value),
        },
        {
            field: 'endDate',
            headerName: 'End Date',
            flex: 2,
            minWidth: 100,
            sortable: false,
            valueFormatter: ({ value }) => toAU(value),
        },
        {
            field: 'action',
            headerName: '',
            flex: 0.1,
            sortable: false,
            width: 0,
            renderCell: (p) => {
                const onClick = () =>
                    setDates((prev: any) =>
                        prev.filter((v: any) => v?.id !== p?.id),
                    );
                return (
                    <CustomIconButton
                        sx={{
                            backgroundColor: 'transparent',
                            width: '25px',
                            height: '25px',
                            '&:hover': {
                                cursor: 'pointer',
                                color:
                                    p?.row.isAvailable === true
                                        ? theme.palette.textAccent.main
                                        : theme.palette.red.main,
                                backgroundColor: 'transparent',
                                background: 'none',
                            },
                        }}
                        onClick={onClick}
                    >
                        <DeleteForeverIcon
                            fontSize='small'
                            sx={{ fontSize: '16px' }}
                        />
                    </CustomIconButton>
                );
            },
        },
    ];

    const singleDay =
        startDate &&
        endDate &&
        isSameDay(new Date(startDate), new Date(endDate));
    const label = singleDay && dates?.length === 1 ? 'Date' : 'Dates';
    const value =
        startDate && endDate
            ? singleDay
                ? format(new Date(startDate), 'dd/MM/yyyy')
                : `${format(new Date(startDate), 'dd/MM/yyyy')} - ${format(
                      new Date(endDate),
                      'dd/MM/yyyy',
                  )}`
            : '';

    const showBookerAutocomplete = isGuestAdmin;
    const showCommercial =
        experienceDetails.type === 'commercial' &&
        (!loggedInUser?.role?.includes('owner') || isOnBehalf);

    // show if experience is boat or property, and onBehalf or guest booking
    const showAdditionalServices =
        ([ExperienceType.Boat, ExperienceType.Property].includes(
            experienceDetails?.type,
        ) ||
            (experienceDetails?.type === ExperienceType.Commercial &&
                ['entireVenue', 'meetingRoom'].includes(typeKey)) ||
            isMeetingRoom) &&
        (!isOwner || (isOwner && isOnBehalf));

    const showNonCommercial = experienceDetails.type !== 'commercial';
    const renderFees = () => {
        if (!fees) return false;
        if (loggedInUser?.role?.includes('owner')) return !!isOnBehalf;
        return true;
    };
    const showFees =
        experienceDetails?.charges.length !== 0 &&
        !experienceDetails?.billingExcluded &&
        renderFees();

    const fullAddress = `${experienceDetails?.streetAddress}  ${experienceDetails?.suburb} ${experienceDetails?.postcode} ${experienceDetails?.state} ${experienceDetails?.countryCode}`;

    useEffect(() => {
        if (
            typeKey === 'desk' ||
            (typeKey === 'entireVenue' && !hasByZoneCleaningFee)
        )
            setValue('spaces', []);
    }, [typeKey]);

    const reservationDateOptions = getDateArray(startDate, endDate);

    const contractorsSet = new Set(
        allJobsArray
            .map((job) => job?.contractor?.id)
            .filter((id): id is number => typeof id === 'number' && id > 0),
    );

    const experienceContractors = [...(experienceDetails?.contractors ?? [])];

    const contractorIds = Array.from(contractorsSet);

    const selectedContractors = experienceContractors.filter((contractor) =>
        contractorIds.includes(contractor.id),
    );
    const selectedContractorsWithPolicy = selectedContractors.filter(
        (contractor) => contractor.cancellationPolicy,
    );

    const spacesWithAvailability = useMemo(
        () =>
            getSpacesWithAvailability({
                experienceDetails,
                hasByZoneCleaningFee,
                spaceType: typeKey,
            }),
        [experienceDetails, hasByZoneCleaningFee, typeKey],
    );

    const showSpaces = useMemo(
        () =>
            shouldShowSpaces({
                experienceDetails,
                hasByZoneCleaningFee,
                spaceType: typeKey,
                isMeetingRoom,
                isOwner,
                isOnBehalf,
            }),
        [
            experienceDetails,
            hasByZoneCleaningFee,
            typeKey,
            isMeetingRoom,
            isOwner,
            isOnBehalf,
        ],
    );

    const shouldDisableAddon =
        isOwner &&
        isOnBehalf &&
        experienceDetails?.availabilityMode === AvailabilityMode.AddOn;

    const showAddOnDateWarning =
        isAddOn &&
        params.mainBooking &&
        (startDate < params.mainBooking.startDate ||
            endDate > params.mainBooking.endDate);

    // sync dates
    useEffect(() => {
        if (state?.dates)
            setDates(
                state.dates.map((d: any, i: number) => ({
                    id: i,
                    startDate: toISODate(d.startDate),
                    endDate: toISODate(d.endDate),
                })),
            );
    }, [state]);

    return (
        <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit, onInvalid)}>
                <Box
                    sx={{
                        pt: isItineraryItemForm ? 0 : { xs: 1, sm: 1.5 },
                        pb: isItineraryItemForm ? 0 : { xs: 2, sm: 3 },
                    }}
                >
                    <Container
                        maxWidth='lg'
                        sx={{
                            px: isItineraryItemForm
                                ? 0
                                : { xs: 1.5, sm: 2, md: 3 },
                        }}
                    >
                        <Paper
                            elevation={0}
                            square
                            sx={{
                                backgroundColor: theme.palette.neutral.main,
                                ...(isItineraryItemForm
                                    ? { p: 0 }
                                    : {
                                          px: { xs: 2, sm: 3, md: 3.5 },
                                          pb: { xs: 2, sm: 3, md: 3.5 },
                                          pt: { xs: 1.5, sm: 2, md: 2 },
                                      }),
                                borderRadius: 2,
                                border: 'none',
                                boxShadow: 'none',
                            }}
                        >
                            {!isItineraryItemForm && (
                                <Box
                                    sx={{
                                        position: 'relative',
                                        mb: { xs: 1, sm: 1.5 },
                                    }}
                                >
                                    <Typography
                                        variant='h1'
                                        sx={{
                                            textDecoration: 'none',
                                            textAlign: 'left',
                                            fontSize: {
                                                xs: '20px',
                                                sm: '22px',
                                                md: '24px',
                                            },
                                            lineHeight: 1.2,
                                            textTransform: 'capitalize',
                                            pr: { xs: 5, sm: 0 },
                                        }}
                                    >
                                        {isOwner
                                            ? 'Reserve'
                                            : 'Reservation Request'}
                                    </Typography>
                                    <BackButton />
                                </Box>
                            )}

                            <Grid
                                container
                                columnSpacing={{ xs: 0, sm: 2, md: 3 }}
                                rowSpacing={{ xs: 2, sm: 3 }}
                                alignItems='stretch'
                                sx={{
                                    bgcolor: isItineraryItemForm
                                        ? theme.palette.form.background
                                        : theme.palette.neutral.main,
                                }}
                            >
                                <Grid
                                    item
                                    xs={12}
                                    md={6}
                                    lg={7}
                                    sx={{
                                        display: 'flex',
                                        bgcolor: isItineraryItemForm
                                            ? theme.palette.form.background
                                            : 'primary.white',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            bgcolor: isItineraryItemForm
                                                ? theme.palette.form.background
                                                : theme.palette.neutral.main,
                                            ...(isItineraryItemForm
                                                ? {
                                                      py: { xs: 2, sm: 3 },
                                                      px: 0,
                                                  }
                                                : {
                                                      pt: { xs: 0.5, sm: 1 },
                                                      pb: { xs: 2, sm: 3 },
                                                      px: {
                                                          xs: 2,
                                                          sm: 3,
                                                      },
                                                  }),
                                            borderRadius: 2,
                                            width: '100%',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: { xs: 1.75, sm: 2 },
                                        }}
                                    >
                                        <Stack
                                            gap={{ xs: 1.5, sm: 2 }}
                                            sx={reservationRequestFormFieldsSx(
                                                theme,
                                            )}
                                        >
                                            {isOwner && (
                                                <Grid>
                                                    <Grid pb='20px'>
                                                        <CustomInputLabel
                                                            htmlFor='on-behalf__input'
                                                            id='on-behalf__label'
                                                        >
                                                            Is this reservation
                                                            on behalf of
                                                            someone?
                                                        </CustomInputLabel>
                                                        <FormSelect
                                                            labelId='on-behalf__label'
                                                            id='on-behalf__input'
                                                            name='onBehalf'
                                                            control={control}
                                                            defaultValue={false}
                                                            options={[
                                                                {
                                                                    value: true,
                                                                    label: 'Yes',
                                                                },
                                                                {
                                                                    value: false,
                                                                    label: 'No',
                                                                },
                                                            ]}
                                                        />
                                                    </Grid>
                                                    {!isOnBehalf && (
                                                        <Grid>
                                                            <Grid pb='20px'>
                                                                <CustomInputLabel
                                                                    htmlFor='reason__input'
                                                                    id='reason__label'
                                                                >
                                                                    Reservation
                                                                    Reason
                                                                </CustomInputLabel>
                                                                <FormSelect
                                                                    labelId='reason__label'
                                                                    id='reason__input'
                                                                    name='reason'
                                                                    control={
                                                                        control
                                                                    }
                                                                    defaultValue='other'
                                                                    options={[
                                                                        {
                                                                            value: 'cleaning',
                                                                            label: 'Cleaning',
                                                                        },
                                                                        {
                                                                            value: 'maintenance',
                                                                            label: 'Maintenance',
                                                                        },
                                                                        {
                                                                            value: 'publicHoliday',
                                                                            label: 'Public Holiday',
                                                                        },
                                                                        {
                                                                            value: 'staffAway',
                                                                            label: 'Staff Away',
                                                                        },
                                                                        {
                                                                            value: 'shutdownPeriod',
                                                                            label: 'Shutdown Period',
                                                                        },
                                                                        {
                                                                            value: 'other',
                                                                            label: 'Other',
                                                                        },
                                                                    ]}
                                                                />
                                                            </Grid>
                                                            <Grid pb='20px'>
                                                                <Box
                                                                    display='flex'
                                                                    justifyContent='space-between'
                                                                >
                                                                    <CustomInputLabel
                                                                        required={
                                                                            watch(
                                                                                'reason',
                                                                            ) ===
                                                                                'other' &&
                                                                            watch(
                                                                                'notes',
                                                                            )
                                                                                ?.length ===
                                                                                0
                                                                        }
                                                                        htmlFor='notes__input'
                                                                    >
                                                                        Additional
                                                                        Notes
                                                                    </CustomInputLabel>
                                                                    {watch(
                                                                        'notes',
                                                                    )?.length >
                                                                        0 && (
                                                                        <Typography
                                                                            color='primary'
                                                                            variant='subtitle2'
                                                                        >
                                                                            {`Characters left: ${
                                                                                300 -
                                                                                watch(
                                                                                    'notes',
                                                                                )
                                                                                    ?.length
                                                                            }`}
                                                                        </Typography>
                                                                    )}
                                                                </Box>
                                                                <FormTextField
                                                                    id='notes__input'
                                                                    name='notes'
                                                                    placeholder={
                                                                        watch(
                                                                            'reason',
                                                                        ) ===
                                                                        'other'
                                                                            ? "Please enter 'Other' reason"
                                                                            : ''
                                                                    }
                                                                    control={
                                                                        control
                                                                    }
                                                                    multiline
                                                                    rows={4}
                                                                    inputProps={{
                                                                        maxLength: 300,
                                                                    }}
                                                                />
                                                            </Grid>
                                                            {showStaffBlocks && (
                                                                <OwnerAttendeesSection
                                                                    experienceDetails={
                                                                        experienceDetails
                                                                    }
                                                                    usesFaceRec={
                                                                        usesFaceRec
                                                                    }
                                                                    isEditing={
                                                                        false
                                                                    }
                                                                />
                                                            )}
                                                        </Grid>
                                                    )}
                                                </Grid>
                                            )}
                                            {showAddOnDateWarning && (
                                                <Alert severity='warning'>
                                                    <strong>Warning:</strong>{' '}
                                                    Selected dates fall outside
                                                    of current reservation.
                                                </Alert>
                                            )}
                                            {showBookerAutocomplete && (
                                                <Box
                                                    sx={{
                                                        // Match RightSummary outer card inner padding so this row
                                                        // lines up with the summary thumbnail/header block.
                                                        mt: isOwner
                                                            ? 0
                                                            : {
                                                                  xs: 2,
                                                                  sm: 2.5,
                                                              },
                                                    }}
                                                >
                                                    <BookerSelect
                                                        isGuestAdmin={
                                                            isGuestAdmin
                                                        }
                                                        control={control}
                                                        isFetchingGuestUsers={
                                                            isFetchingGuestUsers
                                                        }
                                                        guestUsers={guestUsers}
                                                        navigate={navigate}
                                                        bookerId={bookerId}
                                                    />
                                                </Box>
                                            )}

                                            {showCommercial && (
                                                <WorkspaceTypeAndMeetingRoom
                                                    control={control}
                                                    availableSpaces={
                                                        availableSpaces
                                                    }
                                                    commercialHasExistingBooking={
                                                        !commercialAvailability?.entireVenueAvailable
                                                    }
                                                    spaceType={typeKey}
                                                    meetingRoomsAvailable={
                                                        commercialAvailability?.meetingRooms ??
                                                        0
                                                    }
                                                />
                                            )}

                                            {showSpaces &&
                                                experienceDetails?.type ===
                                                    ExperienceType.Commercial &&
                                                typeKey !== 'entireVenue' &&
                                                checkinTime &&
                                                checkoutTime && (
                                                    <ReservationSpaces
                                                        key={`reservation-spaces-${
                                                            typeKey || 'none'
                                                        }`}
                                                        collapsible
                                                        hasByZoneCleaningFee={
                                                            hasByZoneCleaningFee
                                                        }
                                                        name='spaces'
                                                        spaces={
                                                            spacesWithAvailability
                                                        }
                                                        bookingStartDate={
                                                            startDate
                                                        }
                                                        bookingEndDate={endDate}
                                                        bookingCheckinTime={
                                                            checkinTime
                                                        }
                                                        bookingCheckoutTime={
                                                            checkoutTime
                                                        }
                                                        experienceDetails={
                                                            experienceDetails
                                                        }
                                                        spaceType={
                                                            typeKey || ''
                                                        }
                                                        isMeetingRoom={
                                                            isMeetingRoom
                                                        }
                                                        handleError={(f, m) =>
                                                            setErrors((p) => ({
                                                                ...p,
                                                                [f]: m,
                                                            }))
                                                        }
                                                        clearError={(f) =>
                                                            setErrors((p) => {
                                                                const {
                                                                    [f]: _,
                                                                    ...rest
                                                                } = p;
                                                                return rest;
                                                            })
                                                        }
                                                    />
                                                )}

                                            {experienceDetails?.type ===
                                                'vehicle' &&
                                                (!isOwner ||
                                                    (isOwner &&
                                                        isOnBehalf)) && (
                                                    <Drivers />
                                                )}

                                            {(!isOwner ||
                                                (isOwner && isOnBehalf)) && (
                                                <TimeSelectors
                                                    control={control}
                                                    experienceType={
                                                        experienceDetails?.type
                                                    }
                                                    checkinT={checkinT}
                                                    checkoutT={checkoutT}
                                                    compareCheckinSettings={
                                                        compareCheckinSettings
                                                    }
                                                    compareCheckoutSettings={
                                                        compareCheckoutSettings
                                                    }
                                                    checkoutError={
                                                        errors.checkoutTime
                                                    }
                                                />
                                            )}

                                            {(!isOwner ||
                                                (isOwner && isOnBehalf)) && (
                                                <VisitTypeField
                                                    control={control}
                                                    isOwner={isOwner}
                                                    beckFamilyCheck={profileDetails?.companyName
                                                        ?.toLowerCase()
                                                        .includes('beck')}
                                                    visitType={
                                                        experienceDetails?.visitType
                                                    }
                                                />
                                            )}

                                            {experienceDetails?.type !==
                                                'vehicle' &&
                                                (!isOwner || isOnBehalf) &&
                                                (typeKey === 'meetingRoom' ||
                                                    typeKey === 'entireVenue' ||
                                                    typeKey === undefined ||
                                                    typeKey === '' ||
                                                    isMeetingRoom) && (
                                                    <>
                                                        <Divider
                                                            sx={{
                                                                mt: 2,
                                                                mb: 2,
                                                                color: theme
                                                                    .palette
                                                                    .faded.main,
                                                            }}
                                                        />
                                                        <GuestsSection
                                                            experienceType={
                                                                experienceDetails?.type
                                                            }
                                                            visitType={
                                                                experienceDetails?.visitType
                                                            }
                                                            startDate={
                                                                startDate
                                                            }
                                                            endDate={endDate}
                                                            adultCount={
                                                                adultCount
                                                            }
                                                            childCount={
                                                                childCount
                                                            }
                                                            maxGuests={
                                                                experienceDetails
                                                                    ?.details
                                                                    ?.maxGuests
                                                            }
                                                            activityGuestMax={
                                                                experienceDetails
                                                                    ?.details
                                                                    ?.quantity
                                                            }
                                                            overnightGuests={
                                                                overnightGuestCount
                                                            }
                                                            overnightMax={
                                                                experienceDetails
                                                                    ?.details
                                                                    ?.overnightGuests
                                                            }
                                                            maxReached={
                                                                MAX_GUEST_COUNT_TEST
                                                            }
                                                            checkIfOvernight={() =>
                                                                MAX_GUEST_COUNT_TEST
                                                            }
                                                        />
                                                    </>
                                                )}

                                            {(!isOwner || isOnBehalf) &&
                                                showStaffBlocks && (
                                                    <>
                                                        <Divider
                                                            sx={{
                                                                mt: 2,
                                                                mb: 2,
                                                                color: theme
                                                                    .palette
                                                                    .faded.main,
                                                            }}
                                                        />
                                                        {isOnBehalf ? (
                                                            <OwnerAttendeesSection
                                                                experienceDetails={
                                                                    experienceDetails
                                                                }
                                                                usesFaceRec={
                                                                    usesFaceRec
                                                                }
                                                                isEditing={
                                                                    false
                                                                }
                                                            />
                                                        ) : (
                                                            <AttendeesSection
                                                                setValue={
                                                                    setValue
                                                                }
                                                                experienceDetails={
                                                                    experienceDetails
                                                                }
                                                                guestCount={
                                                                    guestCount
                                                                }
                                                                showStaffBlocks={
                                                                    showStaffBlocks
                                                                }
                                                                showAdditionalServices={
                                                                    showAdditionalServices
                                                                }
                                                                control={
                                                                    control
                                                                }
                                                                isCatering={
                                                                    isCatering
                                                                }
                                                                isMassage={
                                                                    isMassage
                                                                }
                                                                isAlcohol={
                                                                    isAlcohol
                                                                }
                                                                cateringDetails={
                                                                    cateringDetails
                                                                }
                                                                beverageDetails={
                                                                    beverageDetails
                                                                }
                                                                massageDetails={
                                                                    massageDetails
                                                                }
                                                                reservationDateOptions={
                                                                    reservationDateOptions
                                                                }
                                                            />
                                                        )}
                                                        {isOnBehalf &&
                                                            showAdditionalServices && (
                                                                <Box
                                                                    sx={{
                                                                        mt: 2,
                                                                    }}
                                                                >
                                                                    <AdditionalServices
                                                                        experienceDetails={
                                                                            experienceDetails
                                                                        }
                                                                        control={
                                                                            control
                                                                        }
                                                                        isCatering={
                                                                            isCatering
                                                                        }
                                                                        isMassage={
                                                                            isMassage
                                                                        }
                                                                        isAlcohol={
                                                                            isAlcohol
                                                                        }
                                                                        cateringDetails={
                                                                            cateringDetails
                                                                        }
                                                                        beverageDetails={
                                                                            beverageDetails
                                                                        }
                                                                        massageDetails={
                                                                            massageDetails
                                                                        }
                                                                        reservationDateOptions={
                                                                            reservationDateOptions
                                                                        }
                                                                    />
                                                                </Box>
                                                            )}
                                                    </>
                                                )}

                                            {showSpaces &&
                                                (experienceDetails?.type !==
                                                    ExperienceType.Commercial ||
                                                    (experienceDetails?.type ===
                                                        ExperienceType.Commercial &&
                                                        typeKey ===
                                                            'entireVenue')) && (
                                                    <Box
                                                        sx={{
                                                            mt:
                                                                showAdditionalServices &&
                                                                (!isOwner ||
                                                                    isOnBehalf) &&
                                                                showStaffBlocks
                                                                    ? 2
                                                                    : 0,
                                                        }}
                                                    >
                                                        <ReservationSpaces
                                                            key={`reservation-spaces-${
                                                                typeKey ||
                                                                'none'
                                                            }`}
                                                            collapsible
                                                            name='spaces'
                                                            spaces={
                                                                spacesWithAvailability
                                                            }
                                                            hasByZoneCleaningFee={
                                                                hasByZoneCleaningFee
                                                            }
                                                            bookingStartDate={
                                                                startDate
                                                            }
                                                            bookingEndDate={
                                                                endDate
                                                            }
                                                            bookingCheckinTime={
                                                                checkinTime
                                                            }
                                                            bookingCheckoutTime={
                                                                checkoutTime
                                                            }
                                                            experienceDetails={
                                                                experienceDetails
                                                            }
                                                            spaceType={
                                                                typeKey || ''
                                                            }
                                                            isMeetingRoom={
                                                                isMeetingRoom
                                                            }
                                                            handleError={(
                                                                f,
                                                                m,
                                                            ) =>
                                                                setErrors(
                                                                    (p) => ({
                                                                        ...p,
                                                                        [f]: m,
                                                                    }),
                                                                )
                                                            }
                                                            clearError={(f) =>
                                                                setErrors(
                                                                    (p) => {
                                                                        const {
                                                                            [f]: _,
                                                                            ...rest
                                                                        } = p;
                                                                        return rest;
                                                                    },
                                                                )
                                                            }
                                                        />
                                                    </Box>
                                                )}

                                            {(!isOwner ||
                                                (isOwner && !isOnBehalf)) && (
                                                <Divider
                                                    sx={{
                                                        mt: 2,
                                                        mb: 2,
                                                        color: theme.palette
                                                            .faded.main,
                                                    }}
                                                />
                                            )}

                                            <NotesField
                                                control={control}
                                                watch={useWatch}
                                                onBehalf={isOnBehalf}
                                                isOwner={isOwner}
                                            />
                                        </Stack>
                                    </Box>
                                </Grid>

                                <Grid
                                    item
                                    xs={12}
                                    md={6}
                                    lg={5}
                                    sx={{
                                        alignSelf: 'flex-start',
                                        bgcolor: isItineraryItemForm
                                            ? theme.palette.form.background
                                            : theme.palette.neutral.main,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            bgcolor: isItineraryItemForm
                                                ? theme.palette.form.background
                                                : theme.palette.neutral.main,
                                            ...(isItineraryItemForm
                                                ? { p: { xs: 2, sm: 3 } }
                                                : {
                                                      pt: { xs: 0.5, sm: 1 },
                                                      pb: { xs: 2, sm: 3 },
                                                      px: {
                                                          xs: 2,
                                                          sm: 3,
                                                      },
                                                  }),
                                            borderRadius: 2,
                                            height: 'auto',
                                        }}
                                    >
                                        <RightSummary
                                            experienceDetails={
                                                experienceDetails
                                            }
                                            fullAddress={fullAddress}
                                            label={label}
                                            value={value}
                                            dates={dates}
                                            columns={columns}
                                            availability={
                                                commercialAvailability
                                            }
                                            spaceType={typeKey}
                                            locationState={state}
                                            isOwner={isOwner}
                                            isOnBehalf={isOnBehalf}
                                            showFees={showFees}
                                            fees={fees}
                                            spaces={spaces}
                                            checkinTime={checkinTime}
                                            checkoutTime={checkoutTime}
                                        />

                                        {(!loggedInUser?.role?.includes(
                                            'owner',
                                        ) ||
                                            isOnBehalf) &&
                                            (watchedPolicy?.policyId ||
                                                experienceDetails?.policy) && (
                                                <ReservationPolicies />
                                            )}

                                        {contractorJobsArray.length > 0 &&
                                            selectedContractorsWithPolicy.length >
                                                0 && (
                                                <ContractorPolicies
                                                    contractors={
                                                        selectedContractors
                                                    }
                                                    jobs={contractorJobsArray}
                                                />
                                            )}
                                        {shouldDisableAddon && (
                                            <Alert
                                                severity='error'
                                                sx={{ mt: 2 }}
                                            >
                                                This experience can only be
                                                reserved as an add-on.
                                            </Alert>
                                        )}
                                        {!isItineraryItemForm && (
                                            <CustomButton
                                                type='submit'
                                                fullWidth
                                                variant='contained'
                                                color='primary'
                                                loading={isSubmitting}
                                                disabled={
                                                    isSubmitting ||
                                                    loggedInUser?.viewOnly ||
                                                    shouldDisableAddon
                                                }
                                                sx={{ marginTop: 2 }}
                                            >
                                                {isOwner ||
                                                !experienceDetails.requiresBookingApproval
                                                    ? 'Reserve'
                                                    : 'Request'}
                                            </CustomButton>
                                        )}
                                    </Box>
                                </Grid>
                            </Grid>
                        </Paper>
                    </Container>
                </Box>
            </form>
            <CancelModal
                open={navigationBlockerOpen}
                onClose={handleCancelNavigation}
                onSubmit={handleProceedNavigation}
            />
        </FormProvider>
    );
}
